// What Home shows (redesign slice 3), worked out from data the app already
// loads. Pure functions, so tests/home.test.mjs can check them directly.
import { DAYS, DINING_OUT, MISSING_RECIPE_LABEL, PLANNABLE_DISH_TYPES } from "./constants";
import { getWeekStartDate, toISODate } from "./utils";
import { normalizeDishes, weekMealCount } from "./planUtils";
import { mealLabel, type LeftoverSource } from "./planScaling";
import type { Day, DishType, MealSlot, Purveyor, PurveyorBox, PurveyorHours, PurveyorLocation, Recipe, RecipeById, WeekPlan } from "./types";

// ------------------------------------------------------------ dates ---

export const dayName = (date: Date): Day => DAYS[(date.getDay() + 6) % 7];

export function addDays(date: Date, n: number): Date {
  const d = new Date(date);
  d.setDate(d.getDate() + n);
  return d;
}

// "Sat, Oct 3"
export const formatShortDate = (date: Date): string => date.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });

const fromISO = (iso: string): Date => new Date(`${iso}T00:00:00`);

// "08:00:00" -> "8 am", "13:30" -> "1:30 pm", "" -> "".
export function formatTime(time: string): string {
  const m = /^(\d{1,2}):(\d{2})/.exec(time || "");
  if (!m) return "";
  const h = Number(m[1]);
  const suffix = h < 12 ? "am" : "pm";
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return m[2] === "00" ? `${h12} ${suffix}` : `${h12}:${m[2]} ${suffix}`;
}

export interface HomeDay {
  label: "Today" | "Tomorrow";
  date: Date;
  day: Day;
  // The plan week this day belongs to. Tomorrow can fall in next week.
  weekStart: string;
}

export function homeDays(now: Date, weekStartDay: Day): HomeDay[] {
  return (["Today", "Tomorrow"] as const).map((label, i) => {
    const date = addDays(now, i);
    date.setHours(0, 0, 0, 0);
    return { label, date, day: dayName(date), weekStart: toISODate(getWeekStartDate(date, weekStartDay)) };
  });
}

// ------------------------------------------------------------ meals ---

export interface PlannedMeal {
  slot: MealSlot;
  recipeId: string | null; // null for dining out or a recipe the viewer can't see
  title: string;
  minutes: number; // the main dish's active + cook time; 0 when not cooked
  withTitles: string[]; // the slot's other dishes, e.g. a side
  isLeftover: boolean;
  // "From last night's dinner", for leftovers with a known source.
  sourceLabel: string;
  isDiningOut: boolean;
}

// The wireframe leads with dinner, the meal most people plan around.
export const HOME_SLOT_ORDER: MealSlot[] = ["dinner", "lunch", "breakfast"];

const totalMinutes = (r: Recipe | undefined) => (r ? (r.activeTime || 0) + (r.cookTime || 0) : 0);

export function mealsForDay(week: WeekPlan | undefined, day: Day, recipeById: RecipeById, now: Date = new Date()): PlannedMeal[] {
  const meals: PlannedMeal[] = [];
  for (const slot of HOME_SLOT_ORDER) {
    const dishes = normalizeDishes(week?.days?.[day]?.[slot]) as Partial<Record<DishType, string>>;
    const filled = PLANNABLE_DISH_TYPES.filter((dt) => dishes[dt]);
    if (filled.length === 0) continue;
    const [mainType, ...otherTypes] = filled;
    const mainId = dishes[mainType] as string;
    const isLeftover = !!week?.leftovers?.[`${day}|${slot}|${mainType}`];
    const source: LeftoverSource | undefined = isLeftover ? week?.leftoverSources?.[`${day}|${slot}|${mainType}`] : undefined;
    if (mainId === DINING_OUT) {
      meals.push({ slot, recipeId: null, title: "Eating out", minutes: 0, withTitles: [], isLeftover: false, sourceLabel: "", isDiningOut: true });
      continue;
    }
    const main = recipeById(mainId);
    meals.push({
      slot,
      recipeId: main ? mainId : null,
      title: main ? main.title : MISSING_RECIPE_LABEL,
      minutes: isLeftover ? 0 : totalMinutes(main),
      withTitles: otherTypes.map((dt) => recipeById(dishes[dt] as string)?.title).filter((t): t is string => !!t),
      isLeftover,
      sourceLabel: source ? `From ${mealLabel(source, now)}` : "",
      isDiningOut: false,
    });
  }
  return meals;
}

export const weekHasMeals = (week: WeekPlan | undefined): boolean => weekMealCount(week) > 0;

// ------------------------------------------------------- new recipes ---

// The most recently added recipes in the library, newest first.
export function newestRecipes(recipes: Recipe[], limit = 3): Recipe[] {
  return [...recipes]
    .filter((r) => r.createdAt)
    .sort((a, b) => (a.createdAt < b.createdAt ? 1 : a.createdAt > b.createdAt ? -1 : 0))
    .slice(0, limit);
}

// ------------------------------------------------ boxes and markets ---

export interface UpcomingBox {
  purveyor: Purveyor;
  box: PurveyorBox;
}

// Boxes from the household's starred sources that haven't arrived yet,
// soonest first.
export function upcomingBoxes(purveyors: Purveyor[], sourceIds: string[], todayISO: string, limit = 3): UpcomingBox[] {
  const starred = new Set(sourceIds);
  return purveyors
    .filter((p) => starred.has(p.id))
    .flatMap((purveyor) =>
      purveyor.boxes.filter((box) => box.deliveryDate && box.deliveryDate >= todayISO && box.status !== "Delivered").map((box) => ({ purveyor, box }))
    )
    .sort((a, b) => (a.box.deliveryDate < b.box.deliveryDate ? -1 : a.box.deliveryDate > b.box.deliveryDate ? 1 : 0))
    .slice(0, limit);
}

export interface MarketDay {
  purveyor: Purveyor;
  date: Date;
  hours: PurveyorHours;
  location: PurveyorLocation | undefined;
}

const inSeason = (h: PurveyorHours, iso: string) => (!h.seasonStart || iso >= h.seasonStart) && (!h.seasonEnd || iso <= h.seasonEnd);

// Each starred source's next open day within `horizonDays` (from its hours
// and their seasons), soonest first.
export function upcomingMarketDays(purveyors: Purveyor[], sourceIds: string[], now: Date, horizonDays = 14, limit = 3): MarketDay[] {
  const starred = new Set(sourceIds);
  const start = new Date(now);
  start.setHours(0, 0, 0, 0);
  const found: MarketDay[] = [];
  for (const purveyor of purveyors) {
    if (!starred.has(purveyor.id) || purveyor.hours.length === 0) continue;
    for (let i = 0; i <= horizonDays; i++) {
      const date = addDays(start, i);
      const iso = toISODate(date);
      const hours = purveyor.hours
        .filter((h) => h.dayOfWeek === dayName(date) && inSeason(h, iso))
        .sort((a, b) => (a.openTime < b.openTime ? -1 : 1))[0];
      if (hours) {
        found.push({ purveyor, date, hours, location: purveyor.pickupLocations.find((l) => l.id === hours.locationId) });
        break;
      }
    }
  }
  return found.sort((a, b) => a.date.getTime() - b.date.getTime()).slice(0, limit);
}

export const isoToDate = fromISO;
